import React, { useState, useEffect, useRef } from 'react'

// Libs
import { useSelector } from 'react-redux'
import { isTomorrow, format, startOfWeek, endOfWeek, subWeeks, addWeeks, eachDayOfInterval } from 'date-fns'
import DayPicker from 'react-day-picker'
import pt from 'date-fns/locale/pt-BR'
import Sticky from 'react-sticky-el'
import clsx from 'clsx'

// Settings
import { FORMAT, WEEKDAYS_SHORT, MONTHS } from '../../settings/dayPicker'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { filters } from './settings'

// Components
import { useDimensions, Typography, Spinner } from '@telavita-core/react-design-kit'
import { CoreIcons } from 'components'
import { MoreFiltersModal } from 'containers/ModalManager/Modals/MoreFiltersModal'

// Assets
import { ReactComponent as IconUp } from 'static/svg/chevron-down.svg'
import { ReactComponent as IconClearDesktop } from 'static/svg/clear-desktop.svg'
import { ReactComponent as IconClearMobile } from 'static/svg/clear-mobile.svg'
import { ScheduleContainerFrequencyOptions } from 'containers/ScheduleContainer/_settings'

const FilterHeader = ({
  skills,
  selectedDay,
  selectedSkill,
  isPsiq,
  onSelectDate,
  onSelectSkill,
  moreFiltersCounter,
  searchInfo,
}) => {
  const frequency = useSelector((state) => state.auth.planSelected?.plan_group.frequency)
  const planGroupCategory = useSelector((state) => state.auth.planSelected?.plan_group.category)
  const { CORPORATE_LIMITED, CORPORATE_UNLIMITED } = PlanGroupCategoryEnum
  const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(planGroupCategory)

  const { isMobile } = useDimensions()
  const filterHeaderRef = useRef(null)
  const tabPanelRef = useRef(null)
  const clearButtonRef = useRef(null)

  // Filter Header
  const [currentActiveFilter, setCurrentFilterActive] = useState()
  const [currentHoverFilter, setCurrentHoverFilter] = useState()

  // Experience Filter
  const [searchSkill, setSearchSkill] = useState('')
  const [currentSelectedSkill, setCurrentSelectedSkill] = useState(
    selectedSkill || ''
  )
  const [filteredSkills, setFilteredSkills] = useState([])

  // MoreFilter
  const [openMoreFilterModal, setOpenMoreFilterModal] = useState(false)

  const iconStyleDayFilter = clsx('FilterHeader__DayFilter__icon', {
    'FilterHeader__DayFilter__icon--down': currentActiveFilter !== filters.DAY,
  })

  const selectedDayIsTomorrow = isTomorrow(selectedDay, 1)

  const currentSkills =
    searchSkill.length > 0 && !currentSelectedSkill ? filteredSkills : skills

  const experiencesInputRef = useRef(null)

  const filterButtonLeft = filterHeaderRef.current
    ? filterHeaderRef.current.getBoundingClientRect().left
    : 0

  const filtersLeft = {
    Experiences: filterButtonLeft,
    Day: filterButtonLeft + 284,
    More: filterButtonLeft + 293 * 2,
  }

  const panelLeftPosition =
    filterHeaderRef.current && currentActiveFilter
      ? filtersLeft[currentActiveFilter]
      : 0

  const useOutsideAlerter = (ref) => {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !filterHeaderRef.current.contains(event.target)
        ) {
          setCurrentFilterActive(null)
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])
  }

  const handleClearSearch = () => {
    setCurrentFilterActive(filters.EXPERIENCES)
    setSearchSkill('')
    setCurrentSelectedSkill('')
    experiencesInputRef.current.focus()
  }

  const handleDayClick = (day, { disabled }) => {
    if (disabled) return

    onSelectDate(day)
    setCurrentFilterActive(null)
  }

  const onChangeActiveFilter = (filter) => {
    if (currentActiveFilter === filter && filter !== filters.EXPERIENCES) {
      setCurrentFilterActive(null)
    } else {
      setCurrentFilterActive(filter)
      if (filter === filters.EXPERIENCES) experiencesInputRef.current.focus()
    }
  }

  useEffect(() => {
    if (searchSkill.length > 0) {
      const filtered = skills.filter((theme) =>
        theme.name.toLowerCase().includes(searchSkill.toLowerCase())
      )
      setFilteredSkills(filtered)
    } else {
      setFilteredSkills([])
      setSearchSkill('')
    }

  }, [searchSkill, skills])

  useEffect(() => {
    if (isMobile) {
      if (
        currentActiveFilter === filters.EXPERIENCES ||
        currentActiveFilter === filters.MORE
      ) {
        filterHeaderRef.current.scrollLeft += 164
      } else if (currentActiveFilter === filters.DAY) {
        filterHeaderRef.current.scrollLeft -= 200
      }
    }
   
  }, [currentActiveFilter, isMobile])

  useOutsideAlerter(tabPanelRef)

  function handleDisabledDays() {
    if (!isCorporate) {
      return [
        {
          before: new Date()
        },
      ]
    }
    
    const blockedDays = searchInfo?.patientSchedulesForDayPicker?.flatMap(day => {
      let start
      let end
        
      if (frequency === ScheduleContainerFrequencyOptions.WEEKLY) {
        start = startOfWeek(day)
        end = endOfWeek(day)
      } else if (frequency === ScheduleContainerFrequencyOptions.BIWEEKLY) {
        start = subWeeks(startOfWeek(day), 1)
        end = addWeeks(endOfWeek(day), 1)
      } else if (frequency === ScheduleContainerFrequencyOptions.QUARTERLY){
        start = subWeeks(startOfWeek(day), 12)
        end = addWeeks(endOfWeek(day), 12)
      } else {
        start = day
        end = day
      }
        
      return eachDayOfInterval({
        start: start,
        end: end
      })
    })
      
    return [
      {
        before: new Date()
      },
      ...blockedDays,
    ]
  }

  return (
    <>
      <Sticky stickyClassName='FilterHeader__Sticky'>
        <div className='FilterHeader__Wrapper'>
          <div className={
            clsx({
              'FilterHeader': true,
              'FilterHeader--justifyCenter': isPsiq
            })
          } ref={filterHeaderRef}>
            {!isPsiq && (
              <>
                <button
                  className={clsx({
                    FilterHeader__FilterButton: true,
                    FilterHeader__ExperiencesFilter: true,
                    'FilterHeader__FilterButton--active':
                      currentActiveFilter === filters.EXPERIENCES,
                  })}
                  onClick={() => onChangeActiveFilter(filters.EXPERIENCES)}
                  onMouseEnter={() => setCurrentHoverFilter(filters.EXPERIENCES)}
                  onMouseLeave={() => setCurrentHoverFilter()}
                >
                  <div className='FilterHeader__ExperiencesFilter__InputWrapper'>
                    <Typography
                      customClassName={clsx({
                        FilterHeader__ExperiencesFilter__Title: true,
                        'FilterHeader__ExperiencesFilter__Title--active':
                          currentActiveFilter === filters.EXPERIENCES,
                      })}
                      variant='content3'
                    >
                      Com experiência em
                    </Typography>
                    <input
                      type='text'
                      value={searchSkill || currentSelectedSkill}
                      ref={experiencesInputRef}
                      onChange={(e) => {
                        setSearchSkill(e.target.value)
                        setCurrentSelectedSkill('')
                      }}
                      placeholder='Digite e selecione um tema'
                      className='FilterHeader__ExperiencesFilter__Input'
                    />
                  </div>
                  {(searchSkill ||
                    (currentSelectedSkill &&
                      currentActiveFilter === filters.EXPERIENCES)) && (
                    <button
                      className='FilterHeader__ExperiencesFilter__ClearButton'
                      onClick={() => handleClearSearch()}
                      ref={clearButtonRef}
                    >
                      {isMobile ? <IconClearMobile /> : <IconClearDesktop />}
                    </button>
                  )}
                </button>
                <div
                  className={clsx({
                    FilterHeader__Divider: true,
                    'FilterHeader__Divider--hidden':
                      isPsiq ||
                      [filters.EXPERIENCES, filters.DAY].includes(
                        currentActiveFilter || currentHoverFilter
                      ),
                  })}
                />
              </>
            )}

            <button
              className={clsx({
                FilterHeader__FilterButton: true,
                FilterHeader__DayFilter: true,
                'FilterHeader__FilterButton--fullWidth': isPsiq,
                'FilterHeader__FilterButton--active':
                  currentActiveFilter === filters.DAY,
              })}
              onClick={() => onChangeActiveFilter(filters.DAY)}
              onMouseEnter={() => setCurrentHoverFilter(filters.DAY)}
              onMouseLeave={() => setCurrentHoverFilter()}
            >
              <div className={
                clsx({
                  'FilterHeader__DayFilter__Container': true,
                  'FilterHeader__DayFilter--fullWidth': !isPsiq
                })
              }>
                <div className='FilterHeader__DayFilter__Container__DateWrapper'>
                  <CoreIcons
                    fill='#1F35B5'
                    height='24px'
                    name='Calendar'
                    viewBox='0 0 24 24'
                    width='24px'
                  />
                  <div className='FilterHeader__DayFilter__Container__DateWrapper__TextContainer'>
                    <Typography
                      customClassName='FilterHeader__DayFilter__Title'
                      variant='content1'
                      weight='bolder'
                    >
                      {format(new Date(selectedDay), 'd MMM', { locale: pt })}
                    </Typography>
                    <Typography
                      customClassName='FilterHeader__DayFilter__Subtitle'
                      variant='content3'
                    >
                      {selectedDayIsTomorrow
                        ? 'amanhã'
                        : format(new Date(selectedDay), 'EEEE', { locale: pt })}
                    </Typography>
                  </div>
                </div>
                <IconUp className={iconStyleDayFilter} />
              </div>
            </button>

            {!isPsiq && (
              <>
                <div
                  className={clsx({
                    FilterHeader__Divider: true,
                    'FilterHeader__Divider--hidden':
                      [filters.DAY, filters.MORE].includes(
                        currentActiveFilter || currentHoverFilter
                      ),
                  })}
                />
                <button
                  className={clsx({
                    FilterHeader__FilterButton: true,
                    'FilterHeader__FilterButton--active':
                      currentActiveFilter === filters.MORE,
                    FilterHeader__MoreFilter: true,
                  })}
                  onClick={() => setOpenMoreFilterModal(true)}
                  onMouseEnter={() => setCurrentHoverFilter(filters.MORE)}
                  onMouseLeave={() => setCurrentHoverFilter()}
                >
                  <div className='FilterHeader__MoreFilter__Container'>
                    <CoreIcons
                      fill='#1F35B5'
                      height='24px'
                      name='Filters'
                      viewBox='0 0 24 24'
                      width='24px'
                    />
                    <Typography
                      className='FilterHeader__MoreFilter__Container__Text'
                      variant='content3'
                    >
                      <strong>Mais filtros</strong>
                      {moreFiltersCounter > 0 && (
                        `(${moreFiltersCounter})`
                      )}
                    </Typography>
                  </div>
                </button>
              </>
            )}
          </div>
          <div
            className='FilterHeader__FilterPanelBackdrop'
            style={{
              left: isMobile ? 0 : panelLeftPosition,
            }}
            ref={tabPanelRef}
          >
            {currentActiveFilter === filters.EXPERIENCES && (
              <div className='FilterHeader__FilterPanel FilterHeader__ExperiencesFilter__Panel'>
                {currentSkills.length === 0 && (
                  <div className='FilterHeader__ExperiencesFilter__NotFound'>
                    <Typography
                      italic
                      variant='content2'
                      customClassName='FilterHeader__ExperiencesFilter__NotFound__Title'
                    >
                      Nenhum resultado
                    </Typography>
                    <Typography
                      onClick={() => handleClearSearch()}
                      variant='content2'
                      weight='bold'
                      customClassName='FilterHeader__ExperiencesFilter__NotFound__Subtitle'
                    >
                      Ver todas as experiências
                    </Typography>
                  </div>
                )}
                <ul className='FilterHeader__ExperiencesFilter__List'>
                  {currentSkills.length > 0 &&
                    currentSkills.map((theme) => (
                      <li
                        className='FilterHeader__ExperiencesFilter__List__Item'
                        key={theme.name}
                        onClick={() => {
                          setSearchSkill('')
                          setCurrentSelectedSkill(
                            theme.name === 'Todos os temas' ? '' : theme.name
                          )
                          onSelectSkill(theme)
                          setCurrentFilterActive(null)
                          experiencesInputRef.current.blur()
                        }}
                      >
                        {theme.name}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {currentActiveFilter === filters.DAY && (
              <div className='FilterHeader__FilterPanel FilterHeader__DayFilter__Panel'>
                {searchInfo?.isLoadingPatientSchedules && (
                  <div className='FilterHeader__DayFilter__Panel__SpinnerWrapper'>
                    <Spinner />
                  </div>
                )}
                <DayPicker
                  disabledDays={handleDisabledDays()}
                  format={FORMAT}
                  onDayClick={handleDayClick}
                  selectedDays={[selectedDay ? selectedDay : null]}
                  initialMonth={selectedDay ? selectedDay : new Date()}
                  weekdaysShort={WEEKDAYS_SHORT}
                  months={MONTHS}
                  onMonthChange={(month) => searchInfo?.handleChangeMonthInCalendar(month)}
                  modifiers={{ schedule: searchInfo?.patientSchedulesForDayPicker }}
                  fromMonth={new Date()}
                />
              </div>
            )}
          </div>
        </div>
      </Sticky>
      {openMoreFilterModal && 
        <MoreFiltersModal 
          handleCloseMoreFiltersModal={() => setOpenMoreFilterModal(false)}
          searchInfo={{
            profileRole: searchInfo?.profileRole,
            startDate: searchInfo?.startDate,
            endDate: searchInfo?.endDate,
          }}
        />
      }
    </>
  )
}

export { FilterHeader }
